import React, { useState, useEffect } from 'react'
import { apiCall } from '../utils/api'

interface Passkey {
  id: number
  deviceName: string
  lastUsedAt: string | null
  createdAt: string
}

interface SecurityInfo {
  has2FA: boolean
  twoFactorEnabledAt: string | null
  passkeys: Passkey[]
}

interface UserSecurityBadgesProps {
  userId: number
  onSecurityChanged?: () => void
}

export const UserSecurityBadges: React.FC<UserSecurityBadgesProps> = ({ userId, onSecurityChanged }) => {
  const [security, setSecurity] = useState<SecurityInfo | null>(null)
  const [loading, setLoading] = useState(true)
  const [showPasskeyDetails, setShowPasskeyDetails] = useState(false)

  useEffect(() => {
    loadSecurityInfo()
  }, [userId])

  const loadSecurityInfo = async () => {
    try {
      setLoading(true)
      const response = await apiCall(`/api/auth/admin/users/${userId}/security`)
      if (response.ok) {
        const data = await response.json()
        setSecurity(data)
      }
    } catch (error) {
      console.error('Error loading security info:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleDisable2FA = async () => {
    if (!confirm('Are you sure you want to disable 2FA for this user?')) {
      return
    }

    try {
      const response = await apiCall(`/api/auth/admin/users/${userId}/2fa`, {
        method: 'DELETE'
      })

      if (response.ok) {
        await loadSecurityInfo()
        if (onSecurityChanged) onSecurityChanged()
      } else {
        const data = await response.json()
        alert('Failed to disable 2FA: ' + (data.error || 'Unknown error'))
      }
    } catch (error) {
      console.error('Error disabling 2FA:', error)
      alert('Failed to disable 2FA')
    }
  }

  const handleDeletePasskey = async (passkeyId: number, deviceName: string) => {
    if (!confirm(`Are you sure you want to delete the passkey "${deviceName}"?`)) {
      return
    }

    try {
      const response = await apiCall(`/api/auth/admin/users/${userId}/passkeys/${passkeyId}`, {
        method: 'DELETE'
      })

      if (response.ok) {
        await loadSecurityInfo()
        if (onSecurityChanged) onSecurityChanged()
      } else {
        const data = await response.json()
        alert('Failed to delete passkey: ' + (data.error || 'Unknown error'))
      }
    } catch (error) {
      console.error('Error deleting passkey:', error)
      alert('Failed to delete passkey')
    }
  }

  if (loading) {
    return (
      <div className="border-t border-gray-200 dark:border-gray-700 pt-3 mt-3">
        <div className="flex items-center space-x-2 text-sm text-gray-500 dark:text-gray-400">
          <div className="animate-spin rounded-full h-4 w-4 border-2 border-gray-300 border-t-blue-500"></div>
          <span>Loading security...</span>
        </div>
      </div>
    )
  }

  if (!security) {
    return (
      <div className="border-t border-gray-200 dark:border-gray-700 pt-3 mt-3">
        <p className="text-xs text-red-500 dark:text-red-400">Failed to load security info</p>
      </div>
    )
  }

  const hasAnySecurity = security.has2FA || security.passkeys.length > 0

  return (
    <div className="border-t border-gray-200 dark:border-gray-700 pt-3 mt-3">
      <div className="flex items-center justify-between mb-2">
        <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">Security</h4>
      </div>

      {!hasAnySecurity && (
        <p className="text-xs text-gray-500 dark:text-gray-400 italic">No security features enabled</p>
      )}

      <div className="space-y-2">
        {/* 2FA Badge */}
        {security.has2FA && (
          <div className="flex items-center justify-between bg-green-50 dark:bg-green-900/20 px-3 py-2 rounded-lg">
            <div className="flex items-center space-x-2">
              <svg className="h-4 w-4 text-green-600 dark:text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
              <span className="text-xs font-medium text-green-800 dark:text-green-300">2FA Enabled</span>
            </div>
            <button
              onClick={handleDisable2FA}
              className="text-xs text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 font-medium"
            >
              Disable
            </button>
          </div>
        )}

        {/* Passkeys Badge */}
        {security.passkeys.length > 0 && (
          <div className="bg-blue-50 dark:bg-blue-900/20 px-3 py-2 rounded-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <svg className="h-4 w-4 text-gray-900 dark:text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                </svg>
                <span className="text-xs font-medium text-blue-800 dark:text-blue-300">
                  {security.passkeys.length} Passkey{security.passkeys.length > 1 ? 's' : ''}
                </span>
              </div>
              <button
                onClick={() => setShowPasskeyDetails(!showPasskeyDetails)}
                className="text-xs text-gray-900 hover:text-blue-700 dark:text-gray-600 dark:hover:text-blue-300 font-medium"
              >
                {showPasskeyDetails ? 'Hide' : 'Manage'}
              </button>
            </div>

            {/* Passkey Details */}
            {showPasskeyDetails && (
              <div className="mt-2 space-y-1 border-t border-blue-100 dark:border-blue-800 pt-2">
                {security.passkeys.map((passkey) => (
                  <div key={passkey.id} className="flex items-center justify-between text-xs">
                    <span className="text-gray-700 dark:text-gray-300">{passkey.deviceName}</span>
                    <button
                      onClick={() => handleDeletePasskey(passkey.id, passkey.deviceName)}
                      className="text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 font-medium"
                    >
                      Delete
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
